import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { treatmentSvcAxiosInstance } from "@/constants";
import type {
    CreateTreatmentRequest,
    Treatment,
    TreatmentListResponse,
    TreatmentQueryParams,
    UpdateTreatmentRequest,
} from "@/types/treatment.types";
import { invalidateListQueries } from "@/utils/query-helpers";

// Query keys for React Query cache management
const queryKeys = {
    treatments: () => ["treatments"] as const,
    treatment: (id: string) => ["treatments", id] as const,
    treatmentList: (params: TreatmentQueryParams) => ["treatments", "list", params] as const,
};

// API endpoints (Treatment Service)
const endpoints = {
    // Get all treatments with optional filtering and pagination
    getTreatments: async (params: TreatmentQueryParams = {}) => {
        const response = await treatmentSvcAxiosInstance.get<TreatmentListResponse>("/treatments", {
            params: {
                page: params.page,
                limit: params.limit,
                search: params.search,
            },
        });
        return response.data;
    },

    // Get a single treatment by ID
    getTreatment: async (id: string) => {
        const response = await treatmentSvcAxiosInstance.get<{ data: Treatment }>(`/treatments/${id}`);
        return response.data.data;
    },

    // Create a new treatment
    createTreatment: async (data: CreateTreatmentRequest) => {
        const response = await treatmentSvcAxiosInstance.post<{ data: Treatment }>("/treatments", data);
        return response.data.data;
    },

    // Update an existing treatment
    updateTreatment: async ({ id, data }: { id: string; data: UpdateTreatmentRequest }) => {
        const response = await treatmentSvcAxiosInstance.put<{ data: Treatment }>(`/treatments/${id}`, data);
        return response.data.data;
    },

    // Delete a treatment
    deleteTreatment: async (id: string) => {
        const response = await treatmentSvcAxiosInstance.delete<{
            message: string;
            success: boolean;
        }>(`/treatments/${id}`);
        return response.data;
    },
};

// React Query hooks

/**
 * Hook to fetch all treatments with optional filtering and pagination
 */
export const useTreatments = (params: TreatmentQueryParams = {}) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.treatmentList(params),
        queryFn: () => endpoints.getTreatments(params),
        enabled: !!auth, // Only run when authenticated
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to fetch a single treatment by ID
 */
export const useTreatment = (id: string) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.treatment(id),
        queryFn: () => endpoints.getTreatment(id),
        enabled: !!auth && !!id, // Only run when authenticated and ID is provided
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to create a new treatment
 */
export const useCreateTreatment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.createTreatment,
        onSuccess: () => {
            invalidateListQueries(queryClient, "treatments");
        },
    });
};

/**
 * Hook to update an existing treatment
 */
export const useUpdateTreatment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.updateTreatment,
        onSuccess: (data, variables) => {
            // Update the specific treatment in cache
            queryClient.setQueryData(queryKeys.treatment(variables.id), data);
            invalidateListQueries(queryClient, "treatments");
        },
    });
};

/**
 * Hook to delete a treatment
 */
export const useDeleteTreatment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.deleteTreatment,
        onSuccess: (_, id) => {
            // Remove the treatment from cache
            queryClient.removeQueries({ queryKey: queryKeys.treatment(id) });
            invalidateListQueries(queryClient, "treatments");
        },
    });
};
